/**
 * THE ONE PLACE THE ADMIN DECIDES WHAT CATEGORY KEY A WRITE MAY CARRY (task-c2j9yfb4dn, kexnycgswp B2).
 * ======================================================================================================
 *
 * CEO 2026-09-07 / 09-12: category knowledge is keyed by the FULL PATH, leaf rows are deleted, never a
 * full-path-then-leaf fallback. Four admin writes still sent the last path segment
 * (`categoryPath.split(' > ').pop()`) or whatever was typed into a free-text box, and every one of them
 * re-created exactly the bare-leaf row the leaf retirement removes. Backend refusals exist for some of
 * these routes and not others; the ruling for this row is that THE UI MUST NOT DEPEND ON THE BACKEND TO
 * STOP IT, so every write goes through here first.
 *
 * ⚠️ RULING (v), 2026-09-13 (orchestrator): a separator-free name IS a full path when it is a catalogue
 *    ROOT category of a site in the sector ("Bicycles" on a bicycle store is the whole path of its
 *    products). The caller hands in the catalogue roots it knows; a bare name is accepted only when it is
 *    one of them, compared case-insensitively.
 * ⛔ NO ROOTS KNOWN IS NOT THE SAME AS NO ROOTS. When the caller has not loaded a catalogue (`null`), a
 *    bare name is REFUSED with a message that says so — guessing "probably a root" would reopen the very
 *    write this file exists to close. An empty Set means "the catalogue is loaded and has no roots".
 * ⚠️ THE KEY IS NEVER REWRITTEN. This accepts or refuses; it does not trim segments, resolve leaves to
 *    paths, or pick a candidate. Resolution is the backend's (categoryKeyBoundary); a UI that resolves
 *    is a second copy of a rule that can drift.
 */

export const CATEGORY_KEY_RULE =
  'A category key is the full path ("Root > … > Leaf"). A bare name is accepted only when it is a catalogue '
  + 'root category of a site in this sector (ruling v, 2026-09-13). Anything else would create a leaf row beside '
  + 'the full-path one, which is exactly what the leaf retirement deletes.';

/**
 * A plain record, deliberately NOT a discriminated union: the admin's tsconfig is `strict: false`, under which
 * `if (!r.ok)` does not narrow, and every caller would need a cast. `key` is non-null exactly when `ok` is true;
 * `reason` is non-null exactly when `ok` is false.
 */
export interface CategoryWriteKeyResult {
  ok: boolean;
  key: string | null;
  form: 'full_path' | 'catalogue_root' | null;
  reason: string | null;
}

/** The one shape predicate this module shares with the backend's isFullPath: a separator is a full path. */
export function hasPathSeparator(key: string): boolean {
  return key.includes('>');
}

/**
 * The separator-free lines of a catalogue, lower-cased and trimmed — the set a bare name is checked
 * against. Accepts whatever the caller has: catalogue paths, feed category names, or nothing.
 */
export function catalogueRootsFrom(names: Iterable<string | null | undefined>): Set<string> {
  const roots = new Set<string>();
  for (const n of names) {
    if (typeof n !== 'string') continue;
    const t = n.trim();
    if (t === '' || hasPathSeparator(t)) continue;
    roots.add(t.toLowerCase());
  }
  return roots;
}

export function categoryWriteKey(
  input: string | null | undefined,
  catalogueRoots: ReadonlySet<string> | null,
): CategoryWriteKeyResult {
  const key = typeof input === 'string' ? input.trim() : '';
  if (key === '') {
    return { ok: false, key: null, form: null, reason: `The category key is empty. ${CATEGORY_KEY_RULE}` };
  }
  if (hasPathSeparator(key)) {
    return { ok: true, key, form: 'full_path', reason: null };
  }
  if (catalogueRoots === null) {
    return {
      ok: false, key: null, form: null,
      reason: `"${key}" has no separator, and the catalogue for this site is not loaded, so it cannot be confirmed as a `
        + `root category. Nothing was sent. ${CATEGORY_KEY_RULE}`,
    };
  }
  if (catalogueRoots.has(key.toLowerCase())) {
    return { ok: true, key, form: 'catalogue_root', reason: null };
  }
  return {
    ok: false, key: null, form: null,
    reason: `"${key}" is a bare category name, not a full path, and it is not a catalogue root category of this site. `
      + `Nothing was sent. ${CATEGORY_KEY_RULE}`,
  };
}
